import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Input } from "../../../components/input/input";
import styles from "../_styles/authPage.module.css";
import { Button } from "../../../components/button/button";
import { Spring } from "../../../components/spring/spring";
import AuthAPI from "../../../api/AuthAPI";
import UserAPI from "../../../api/UserAPI";
import { setStatus } from "../../../features/statusReducer";
import { useAnimatedNavigate } from "../../../hooks/useAnimatedNavigate";
import { isValidEmail, normalizeEmail } from "../../../utils/emailValidation";
import { useSearchParams } from "react-router";

import { Text } from "../../../components/text/text";

export const LoginPage = () => {
    const dispatch = useDispatch();
    const navigate = useAnimatedNavigate();

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(false);

    const [password, setPassword] = useState("");
    const [passwordError, setPasswordError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorText, setErrorText] = useState("");

    const [searchParams] = useSearchParams();
    const inviteToken = useMemo(() => {
        return searchParams.get("inviteToken") || null;
    }, [searchParams]);

    useEffect(() => setEmailError(false), [email]);
    useEffect(() => setPasswordError(false), [password]);
    useEffect(() => setErrorText(""), [email, password]);

    const openRegister = () => {
        if (inviteToken) {
            navigate(`/auth/register?inviteToken=${inviteToken}`);
        } else {
            navigate("/auth/register");
        }
    };

    const redirectByStatus = async () => {
        const status = await UserAPI.fetchUserStatus();
        dispatch(setStatus(status));

        if (status.isSuperAdmin) {
            navigate("/superadmin");
            return;
        }

        if (status.isUser) {
            navigate("/");
            return;
        }

        if (status.isAdmin) {
            navigate("/admin");
            return;
        }

        navigate("/auth/register");
    };

    const validate = () => {
        let error = false;
        const normalizedEmail = normalizeEmail(email);

        if (!isValidEmail(normalizedEmail)) {
            setEmailError(true);
            setErrorText("Введите корректный email");
            error = true;
        }

        if (password.length < 6) {
            setPasswordError(true);
            error = true;
        }

        return error;
    }


    const handleSubmit = async (event) => {
        event.preventDefault();
        if (validate()) return;

        setLoading(true);

        try {
            const result = await AuthAPI.login(normalizeEmail(email), password);

            if (result.requires2fa && result.challengeId) {
                const params = new URLSearchParams({
                    challengeId: result.challengeId,
                });

                if (result.expiresIn) {
                    params.set("expiresIn", String(result.expiresIn));
                }

                if (inviteToken) {
                    params.set("inviteToken", inviteToken);
                }

                navigate(`/auth/login-otp?${params.toString()}`);
                return;
            }

            if (inviteToken) {
                try {
                    await AuthAPI.acceptInvite(inviteToken);
                } catch (error) {
                    const detail = error?.response?.data?.detail;
                    if (error?.response?.status !== 409) {
                        setErrorText(detail || "Не удалось привязать аккаунт к кофейне. Обратитесь к администратору.");
                        return;
                    }
                }
            }

            await redirectByStatus();
        } catch (error) {
            setErrorText(error?.response?.data?.detail || "Не удалось выполнить вход");
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <Text as="div" variant="h3" className={styles.title} size={28} weight={700}>
                Вход
            </Text>

            <form onSubmit={handleSubmit} className={styles.form}>
                <Input
                    value={email}
                    setValue={setEmail}
                    placeholder="example@example.com"
                    label="Email"
                    autoComplete="email"
                    error={emailError}
                />

                <Input
                    value={password}
                    setValue={setPassword}
                    label="Пароль"
                    error={passwordError}
                    type="password"
                />

                <Spring />

                <Button
                    buttonType="submit"
                    text={loading ? "Входим..." : "Войти"}
                    disabled={loading}
                />

                <Button
                    type="secondary"
                    text="Нет аккаунта? Зарегистрироваться"
                    onClick={openRegister}
                    disabled={loading}
                />

                {
                    !!errorText &&
                    <Text as="div" align="center" variant="body" className={styles.errorText} size={14} lineHeight="1.3">
                        {
                            typeof errorText === "string" ? errorText : "Произошла ошибка"
                        }
                    </Text>
                }
            </form>
        </>
    );
}